<template>
  <div id="project_task_card" class="h-full self-start">
    <div
      v-if="isConfigured && !isEditing"
      id="tasks_rendering_section"
      class="h-full"
    >
      <ProjectTaskSection
        :project-id="section.config.project_id"
        :view-id="section.config.view_id"
        :external-filter-data="externalFilterData"
        class="w-full h-full"
      />
    </div>

    <div
      v-else
      id="project_selection_section"
      class="flex flex-col justify-between h-full gap-6"
    >
      <div class="flex flex-col gap-6">
        <div id="card_name" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color"> Card Name </span>
          <a-input
            v-model:value="cardName"
            class="input_medium"
            placeholder="Enter a card name"
          />
        </div>

        <div id="select_project" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color">
            Choose a project to display tasks
          </span>

          <a-select
            v-model:value="selectedProjectID"
            class="w-full"
            placeholder="Select a project"
            :options="projectsList"
            :field-names="{ label: 'name', value: 'id' }"
            @change="handleProjectChange"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>

        <div
          v-if="selectedProjectID"
          id="select_view"
          class="flex flex-col gap-[6px]"
        >
          <span class="text-[13px] secondary_color"> Select a view </span>
          <a-select
            v-model:value="selectedViewID"
            class="w-full"
            placeholder="Select a view"
            :options="viewsList"
            :field-names="{ label: 'name', value: 'id' }"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>
      </div>

      <a-button
        class="primary_medium w-fit self-end"
        :loading="saving"
        :disabled="!selectedProjectID || !selectedViewID || !cardName"
        @click="handleSaveChanges"
        >{{ saving ? "Saving.." : "Save changes" }}</a-button
      >
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import store from "@/src/store/index.js";
import DashboardService from "@/src/api/DashboardService.js";
import ViewsServiceV2 from "@/src/api/ViewsServiceV2.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProjectTaskSection from "@/src/components/dashboard_components/sections/ProjectTaskSection.vue";

const props = defineProps({
  source: { type: String, required: true },
  section: { type: Object, required: true },
  rowIndex: { type: Number, required: true },
  sectionIndex: { type: Number, required: true },
  globalUserId: { type: Number, default: null },
});

const saving = ref(false);
const cardName = ref(props.section.config?.card_name || "");
const viewsList = ref([]);
const selectedProjectID = ref(props.section.config?.project_id || null);
const selectedViewID = ref(props.section.config?.view_id || null);

const dashboardDetails = computed(
  () => store.getters["dashboard/dashboardDetails"],
);
const dashboardLayout = computed(
  () => store.getters["dashboard/dashboardLayout"],
);
const isEditing = computed(() =>
  store.getters["dashboard/isSectionEditing"](props.rowIndex, props.sectionIndex),
);
const projectsList = computed(() => store.getters["projects/projects"] || []);

const computedUserId = computed(() => {
  return props.globalUserId;
});

const externalFilterData = computed(() => {
  if (!computedUserId.value) return null;
  return {
    assignees: [computedUserId.value]
  };
});

const isConfigured = computed(() => {
  const hasProjectId = !!props.section.config?.project_id;
  const hasViewId = !!props.section.config?.view_id;
  const hasCardName = !!props.section.config?.card_name;

  return hasProjectId && hasViewId && hasCardName;
});

const handleSaveChanges = async () => {
  try {
    saving.value = true;

    if (props.rowIndex !== null && props.sectionIndex !== null) {
      await store.dispatch("dashboard/updateSectionConfig", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
        config: {
          card_name: cardName.value,
          project_id: selectedProjectID.value,
          view_id: selectedViewID.value,
        },
      });

      store.dispatch("dashboard/clearSectionEditing", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
      });

      const updatedLayout = store.getters["dashboard/dashboardLayout"];

      const payload = {
        name: dashboardDetails.value?.name,
        layout: updatedLayout,
      };

      const response = await DashboardService.builder.updateDashboard(
        dashboardDetails.value?.id,
        payload,
      );

      if (response?.success) {
        await store.dispatch("dashboard/setDashboardDetails", response.data);
      }
    }
  } catch (error) {
    console.error("An error occurred while saving changes: ", error);
  } finally {
    saving.value = false;
  }
};

const toFetchProjects = async () => {
  await store.dispatch("projects/fetchProjects");
};

const fetchViews = async () => {
  if (!selectedProjectID.value) return;

  try {
    const response = await ViewsServiceV2.getViews(
      selectedProjectID.value,
      entityTypes.PROJECTS,
    );
    if (response?.success) {
      const allViews = [
        ...(response?.activeViews || []),
        ...(response?.inActiveViews || []),
      ];
      viewsList.value = allViews;
    }
  } catch (error) {
    console.error("An error occurred while fetching views: ", error);
  }
};

const handleProjectChange = async () => {
  selectedViewID.value = null;
  viewsList.value = [];
  if (selectedProjectID.value) {
    await fetchViews();
  }
};

watch(
  () => props.section.config,
  (newConfig) => {
    cardName.value = newConfig?.card_name || "";
    selectedProjectID.value = newConfig?.project_id || null;
    selectedViewID.value = newConfig?.view_id || null;

    if (isEditing.value) return;

    if (newConfig?.project_id && !viewsList.value.length) {
      fetchViews();
    }
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  toFetchProjects();
  if (selectedProjectID.value) {
    fetchViews();
  }
});
</script>
